import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useAuth } from "../../contexts/AuthContext";
import {
  AdminUser,
  createAdminUser,
  deactivateAdminUser,
  deleteAdminUser,
  impersonateUser,
  listUsers,
  reactivateAdminUser,
  updateAdminUser,
  updateUserRole,
} from "../../services/adminService";

type RoleFilter = "all" | "admin" | "staff" | "customer" | "farmer" | "retailer" | "distributor";
type StatusFilter = "all" | "active" | "inactive";

type UserFormState = {
  full_name: string;
  email: string;
  role: string;
  phone: string;
  address: string;
  password: string;
  is_active: boolean;
  is_verified: boolean;
};

const EMPTY_FORM: UserFormState = {
  full_name: "",
  email: "",
  role: "customer",
  phone: "",
  address: "",
  password: "",
  is_active: true,
  is_verified: false,
};

const parseJson = <T,>(value: string | null): T | null => {
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
};

const getTenantId = () => {
  const tenant = parseJson<{ id?: string }>(localStorage.getItem("currentTenant"));
  const userData = parseJson<{ tenant_id?: string }>(localStorage.getItem("user_data"));
  const user = parseJson<{ tenant_id?: string }>(localStorage.getItem("user"));
  return tenant?.id || userData?.tenant_id || user?.tenant_id || "";
};

const roleOptions = ["customer", "staff", "admin", "farmer", "retailer", "distributor"];

const Users: React.FC = () => {
  const { user, hasCapability } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<RoleFilter>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [submittingForm, setSubmittingForm] = useState(false);
  const [form, setForm] = useState<UserFormState>(EMPTY_FORM);
  const [busyUserId, setBusyUserId] = useState<string | null>(null);

  const tenantId = useMemo(() => getTenantId(), []);

  const canAccess = hasCapability("capability:admin_console");

  const loadUsers = async (isRefresh = false) => {
    if (!canAccess) return;
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    try {
      const data = await listUsers();
      setUsers(data);
    } catch {
      toast.error("Failed to load users");
    } finally {
      if (isRefresh) setRefreshing(false);
      else setLoading(false);
    }
  };

  useEffect(() => {
    if (!canAccess) return;
    loadUsers();
  }, [canAccess]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      const roleOk = roleFilter === "all" ? true : (u.role || "").toLowerCase() === roleFilter;
      const isActive = u.is_active !== false;
      const statusOk =
        statusFilter === "all" ? true : statusFilter === "active" ? isActive : !isActive;
      const queryOk =
        !q ||
        (u.full_name || u.name || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q) ||
        (u.role || "").toLowerCase().includes(q) ||
        (u.id || "").toLowerCase().includes(q) ||
        (u.tenant_id || "").toLowerCase().includes(q);
      return roleOk && statusOk && queryOk;
    });
  }, [users, search, roleFilter, statusFilter]);

  const stats = useMemo(() => {
    const total = users.length;
    const active = users.filter((u) => u.is_active !== false).length;
    const inactive = total - active;
    const admins = users.filter((u) => (u.role || "").toLowerCase() === "admin").length;
    return { total, active, inactive, admins };
  }, [users]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingUser(null);
  };

  const openCreate = () => {
    resetForm();
    setShowForm(true);
  };

  const openEdit = (target: AdminUser) => {
    setEditingUser(target);
    setForm({
      full_name: target.full_name || target.name || "",
      email: target.email || "",
      role: target.role || "customer",
      phone: target.phone || "",
      address: target.address || "",
      password: "",
      is_active: target.is_active !== false,
      is_verified: Boolean(target.is_verified),
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    resetForm();
  };

  const handleSaveForm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.full_name.trim()) {
      toast.error("Full name is required");
      return;
    }
    if (!editingUser && !form.email.trim()) {
      toast.error("Email is required");
      return;
    }
    if (!editingUser && !form.password.trim()) {
      toast.error("Password is required");
      return;
    }

    setSubmittingForm(true);
    try {
      if (editingUser) {
        const updated = await updateAdminUser(editingUser.id, {
          full_name: form.full_name.trim(),
          role: form.role,
          phone: form.phone.trim() || undefined,
          address: form.address.trim() || undefined,
          is_active: form.is_active,
          is_verified: form.is_verified,
          password: form.password.trim() || undefined,
        });
        setUsers((prev) => prev.map((u) => (u.id === updated.id ? { ...u, ...updated } : u)));
        toast.success("User updated");
      } else {
        const created = await createAdminUser({
          full_name: form.full_name.trim(),
          email: form.email.trim(),
          role: form.role,
          password: form.password.trim(),
          phone: form.phone.trim() || undefined,
          address: form.address.trim() || undefined,
          is_active: form.is_active,
          is_verified: form.is_verified,
        });
        setUsers((prev) => [created, ...prev]);
        toast.success("User created");
      }
      closeForm();
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || "Failed to save user");
    } finally {
      setSubmittingForm(false);
    }
  };

  const handleRoleChange = async (target: AdminUser, role: string) => {
    if (!target?.id || role === target.role) return;
    setBusyUserId(target.id);
    try {
      await updateUserRole(target.id, role);
      setUsers((prev) => prev.map((u) => (u.id === target.id ? { ...u, role } : u)));
      toast.success("Role updated");
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || "Failed to update role");
    } finally {
      setBusyUserId(null);
    }
  };

  const handleToggleActive = async (target: AdminUser) => {
    if (!target?.id) return;
    setBusyUserId(target.id);
    try {
      if (target.is_active === false) {
        await reactivateAdminUser(target.id);
        setUsers((prev) => prev.map((u) => (u.id === target.id ? { ...u, is_active: true } : u)));
        toast.success("User reactivated");
      } else {
        await deactivateAdminUser(target.id);
        setUsers((prev) => prev.map((u) => (u.id === target.id ? { ...u, is_active: false } : u)));
        toast.success("User deactivated");
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || "Failed to update user status");
    } finally {
      setBusyUserId(null);
    }
  };

  const handleDelete = async (target: AdminUser) => {
    if (!target?.id) return;
    const confirmed = window.confirm(`Delete user ${target.email}? This cannot be undone.`);
    if (!confirmed) return;
    setBusyUserId(target.id);
    try {
      await deleteAdminUser(target.id);
      setUsers((prev) => prev.filter((u) => u.id !== target.id));
      toast.success("User deleted");
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || "Failed to delete user");
    } finally {
      setBusyUserId(null);
    }
  };

  const handleImpersonate = async (target: AdminUser) => {
    if (!target?.id) return;
    if (user?.id === target.id) {
      toast.error("You are already this user");
      return;
    }
    setBusyUserId(target.id);
    try {
      const res = await impersonateUser(target.id);
      if (!localStorage.getItem("impersonator_token")) {
        const existingToken = localStorage.getItem("auth_token") || "";
        const existingUser = localStorage.getItem("user_data") || "";
        localStorage.setItem("impersonator_token", existingToken);
        localStorage.setItem("impersonator_user", existingUser);
      }
      localStorage.setItem("auth_token", res.access_token);
      localStorage.setItem("user_data", JSON.stringify(res.user));
      if (res.user?.tenant_id) {
        localStorage.setItem("currentTenant", JSON.stringify({ id: res.user.tenant_id }));
      }
      toast.success(`Now viewing as ${res.user.email}`);

      const role = res.user.role;
      if (role === "admin") window.location.href = "/admin/dashboard";
      else if (role === "staff") window.location.href = "/staff/dashboard";
      else window.location.href = "/dashboard";
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || "Impersonation failed");
    } finally {
      setBusyUserId(null);
    }
  };

  if (!canAccess) {
    return (
      <div className="p-8 text-center text-red-600 font-semibold text-lg">
        Access denied. Only admins can manage users.
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 md:p-8 space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">User Management</h1>
          <p className="text-sm text-gray-600 mt-1">
            Tenant-scoped admin user directory and access control.
            {tenantId ? <span className="ml-2 font-mono text-xs">Tenant: {tenantId}</span> : null}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => loadUsers(true)}
            disabled={refreshing}
            className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {refreshing ? "Refreshing..." : "Refresh"}
          </button>
          <button
            onClick={openCreate}
            className="px-4 py-2 rounded-lg bg-green-600 text-white font-semibold hover:bg-green-700"
          >
            Add User
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-xs text-gray-500">Total Users</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{stats.total}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-xs text-gray-500">Active</div>
          <div className="text-2xl font-bold text-green-700 mt-1">{stats.active}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-xs text-gray-500">Inactive</div>
          <div className="text-2xl font-bold text-amber-700 mt-1">{stats.inactive}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-xs text-gray-500">Admins</div>
          <div className="text-2xl font-bold text-blue-700 mt-1">{stats.admins}</div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="grid md:grid-cols-3 gap-3">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, role, tenant, or ID..."
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value as RoleFilter)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white"
          >
            <option value="all">All Roles</option>
            {roleOptions.map((role) => (
              <option key={role} value={role}>
                {role}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white"
          >
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-6 text-gray-600">Loading users...</div>
        ) : filteredUsers.length === 0 ? (
          <div className="p-8">
            <div className="text-lg font-semibold text-gray-900 mb-2">No users found</div>
            <div className="text-sm text-gray-600 mb-4">
              This tenant admin view only shows users within the current tenant scope.
            </div>
            <div className="text-sm text-gray-600">
              Use <span className="font-semibold">Add User</span> to create staff, customer, or admin accounts for this tenant.
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">User</th>
                  <th className="px-4 py-3 text-left font-semibold">Role</th>
                  <th className="px-4 py-3 text-left font-semibold">Status</th>
                  <th className="px-4 py-3 text-left font-semibold">Tenant</th>
                  <th className="px-4 py-3 text-left font-semibold">Verification</th>
                  <th className="px-4 py-3 text-left font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u) => {
                  const rowBusy = busyUserId === u.id;
                  const isActive = u.is_active !== false;
                  return (
                    <tr key={u.id} className="border-t border-gray-100 align-top">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{u.full_name || u.name || "-"}</div>
                        <div className="text-gray-600">{u.email}</div>
                        {u.phone ? <div className="text-xs text-gray-500 mt-1">{u.phone}</div> : null}
                        <div className="text-xs text-gray-400 mt-1 font-mono">{u.id}</div>
                      </td>
                      <td className="px-4 py-3">
                        <select
                          value={u.role || "customer"}
                          onChange={(e) => handleRoleChange(u, e.target.value)}
                          disabled={rowBusy}
                          className="rounded-md border border-gray-300 px-2 py-1 bg-white text-sm"
                        >
                          {roleOptions.map((role) => (
                            <option key={role} value={role}>
                              {role}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${
                            isActive ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"
                          }`}
                        >
                          {isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-mono text-xs text-gray-600">{u.tenant_id || "-"}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${
                            u.is_verified ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {u.is_verified ? "Verified" : "Unverified"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => openEdit(u)}
                            disabled={rowBusy}
                            className="px-3 py-1 rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleToggleActive(u)}
                            disabled={rowBusy}
                            className={`px-3 py-1 rounded-md text-white disabled:opacity-50 ${
                              isActive ? "bg-amber-600 hover:bg-amber-700" : "bg-green-600 hover:bg-green-700"
                            }`}
                          >
                            {isActive ? "Deactivate" : "Reactivate"}
                          </button>
                          <button
                            onClick={() => handleImpersonate(u)}
                            disabled={rowBusy || user?.id === u.id}
                            className="px-3 py-1 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                          >
                            Impersonate
                          </button>
                          <button
                            onClick={() => handleDelete(u)}
                            disabled={rowBusy || user?.id === u.id}
                            className="px-3 py-1 rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl border border-gray-200">
            <form onSubmit={handleSaveForm}>
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    {editingUser ? "Edit User" : "Add User"}
                  </h2>
                  <p className="text-sm text-gray-600">
                    {editingUser
                      ? "Update account details, role, and access state."
                      : "Create a tenant-scoped user account for this business."}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closeForm}
                  className="text-gray-500 hover:text-gray-700"
                >
                  Close
                </button>
              </div>

              <div className="p-6 grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                  <input
                    value={form.full_name}
                    onChange={(e) => setForm((prev) => ({ ...prev, full_name: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    disabled={Boolean(editingUser)}
                    required={!editingUser}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <select
                    value={form.role}
                    onChange={(e) => setForm((prev) => ({ ...prev, role: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white"
                  >
                    {roleOptions.map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {editingUser ? "Reset Password (optional)" : "Password"}
                  </label>
                  <input
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    placeholder={editingUser ? "Leave blank to keep current password" : "Minimum 6 characters"}
                    required={!editingUser}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    value={form.phone}
                    onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <textarea
                    value={form.address}
                    onChange={(e) => setForm((prev) => ({ ...prev, address: e.target.value }))}
                    rows={3}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>

                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))}
                  />
                  Active account
                </label>

                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={form.is_verified}
                    onChange={(e) => setForm((prev) => ({ ...prev, is_verified: e.target.checked }))}
                  />
                  Verified account
                </label>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={closeForm}
                  className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingForm}
                  className="px-4 py-2 rounded-lg bg-green-600 text-white font-semibold hover:bg-green-700 disabled:opacity-50"
                >
                  {submittingForm ? "Saving..." : editingUser ? "Save Changes" : "Create User"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
